import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { useDragAndDropEditorTranslations } from 'modules/dragAndDropEditor/hooks/useDragAndDropEditorTranslations';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { WarningFill16 } from 'ui/icons';
import { ConfirmationPrompt, ModalConfirmationFooter } from 'ui/modal';
import { caption, colors, spacing } from 'ui/theme';
import { useUnsavedChangesPrompt } from 'utils/hooks/useUnsavedChangesPrompt';
import noop from 'utils/noop';
import { UseDirtyCategoriesReturnValue } from './hooks/useDirtyCategories';
import { useManageCustomCategoriesFooterTypeProps } from './hooks/useManageCustomCategoriesFooterTypeProps';
import { useProcessCategoriesUpsert } from './hooks/useProcessCategoriesUpsert';
import { ManageCustomCategoriesFormValues } from './ManageCustomCategoriesFormProvider';
import {
  CategoryFieldToDelete,
  ManageCustomCategoriesConfirmationType,
} from './ManageCustomCategoriesModal.types';

export const UnsavedChangesContainer = styled.div`
  ${caption};
  color: ${colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};
`;

export const MANAGE_CUSTOM_CATEGORIES_FOOTER_ID = 'manage-custom-categories-footer';

export type ManageCustomCategoriesFooterProps = {
  /** Closes the modal if there are no unsaved changes, otherwise prompts the user to confirm */
  onClose: () => void;
  /** Callback to be executed when the modal is closed */
  onCloseCallback?: () => void;
  categoryToDelete?: CategoryFieldToDelete;
  dirtyCategories: UseDirtyCategoriesReturnValue;
  resetCategoryDeletionProcess: () => void;
  processCategoryDeletion: () => void;
  manageCustomCategoriesConfirmationType: ManageCustomCategoriesConfirmationType;
  setManageCustomCategoriesConfirmationType: (type: ManageCustomCategoriesConfirmationType) => void;
};

/**
 * Cancel confirmation prompt in progress
 */
export const ManageCustomCategoriesFooter = ({
  manageCustomCategoriesConfirmationType,
  categoryToDelete = {} as CategoryFieldToDelete,
  dirtyCategories = { hasUnsavedChanges: false, create: [], update: [] },
  onClose,
  onCloseCallback = noop,
  setManageCustomCategoriesConfirmationType,
  processCategoryDeletion,
  resetCategoryDeletionProcess,
}: ManageCustomCategoriesFooterProps) => {
  const props = useManageCustomCategoriesFooterTypeProps({
    onCloseCallback,
    processCategoryDeletion,
    categoryToDelete,
    manageCustomCategoriesConfirmationType,
  });

  const { handleSubmit } = useFormContext<ManageCustomCategoriesFormValues>();
  const { processMessageCategoriesUpsert, isLoading } = useProcessCategoriesUpsert();
  const { hasUnsavedChanges, create, update } = dirtyCategories;

  const { ct } = useDragAndDropEditorTranslations();
  useUnsavedChangesPrompt(hasUnsavedChanges, ct('errorMessage.unsavedPrompt')); // triggered when a user try to leave the page with unsaved changes

  const handleSave = () => {
    const payload = { create, update };
    processMessageCategoriesUpsert(payload, onCloseCallback);
  };

  return (
    <ModalConfirmationFooter
      buttonSize={BUTTON_SIZES.LARGE}
      id={`${MANAGE_CUSTOM_CATEGORIES_FOOTER_ID}-${manageCustomCategoriesConfirmationType}`}
      promptType={ConfirmationPrompt.NONE}
      onCancel={onClose}
      onResume={() => {
        setManageCustomCategoriesConfirmationType(ManageCustomCategoriesConfirmationType.None);
        resetCategoryDeletionProcess();
      }}
      message={
        hasUnsavedChanges && (
          <UnsavedChangesContainer>
            <WarningFill16 color={colors.warning100} />
            There are unsaved changes
          </UnsavedChangesContainer>
        )
      }
      {...props}
    >
      <PrimaryButton
        onClick={handleSubmit(handleSave)}
        size={BUTTON_SIZES.LARGE}
        isLoading={isLoading}
      >
        Save
      </PrimaryButton>
    </ModalConfirmationFooter>
  );
};
